import * as THREE from 'three'
import Terrain, { BlockType } from '../terrain'
import { BiomeType } from '../terrain/noise'
import { Mob } from './Mob'
import { Cow } from './Cow'
import { FineDog } from './FineDog'
import { Pig, Sheep, Chicken, Rabbit } from './PassiveAnimals'
import { HostileMob, Zombie, SkeletonGunner, Spider, Creeper } from './HostileMobs'
import { SproutMole, HostileSproutMole, Something } from './HeadspaceMobs'
import BlockDropManager from '../drops'
import ProjectileManager from './projectiles'
import { InventoryItemType, isFoodItem } from '../items'

// Import texture path
// @ts-ignore
import strawberryCowTex from '../static/textures/entity/cow/strawberry_cow.png'
// @ts-ignore
import pigTex from '../static/textures/entity/pig/pig.png'
// @ts-ignore
import sheepTex from '../static/textures/entity/sheep/sheep.png'
// @ts-ignore
import sheepFurTex from '../static/textures/entity/sheep/sheep_fur.png'
// @ts-ignore
import chickenTex from '../static/textures/entity/chicken.png'

export type AnimalMobType =
  | 'cow'
  | 'fineDog'
  | 'pig'
  | 'sheep'
  | 'chicken'
  | 'rabbit'
  | 'sproutMole'

type SavedVector = {
  x?: number
  y?: number
  z?: number
}

export type SavedAnimal = {
  type?: AnimalMobType
  position?: SavedVector
  health?: number
  following?: boolean
  boarded?: boolean
  arkProgress?: {
    boarded?: boolean
  }
}

type SavedAnimalList = {
  version?: number
  animals?: SavedAnimal[]
}

interface RagdollPart {
  mesh: THREE.Mesh
  velocity: THREE.Vector3
  angularVelocity: THREE.Vector3
  life: number
  maxLife: number
  resting?: boolean
}

export default class MobManager {
  scene: THREE.Scene
  terrain: Terrain
  mobs: Mob[] = []
  overworldMobs: Mob[] = []
  netherMobs: Mob[] = []
  ragdolls: RagdollPart[] = []
  dropManager: BlockDropManager | null = null
  creeperExplosionHandler: ((position: THREE.Vector3) => void) | null = null
  hostileSpawnTimer = 5
  hostileSpawnInterval = 9
  maxHostiles = 5
  passiveSpawnTimer = 10
  passiveSpawnInterval = 20
  maxPassives = 12

  projectiles: ProjectileManager

  constructor(scene: THREE.Scene, terrain: Terrain) {
    this.scene = scene
    this.terrain = terrain
    this.projectiles = new ProjectileManager(scene, terrain)
  }

  private registerMob<T extends Mob>(mob: T): T {
    mob.onDeath = () => {
      this.spawnRagdoll(mob)
    }
    this.mobs.push(mob)
    return mob
  }

  spawnRagdoll(mob: Mob) {
    mob.mesh.updateMatrixWorld(true)

    mob.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.visible) {
        const geom = child.geometry.clone()
        const mat = Array.isArray(child.material)
          ? child.material.map(m => m.clone())
          : child.material.clone()

        if (Array.isArray(mat)) {
          mat.forEach(m => {
            m.transparent = true
          })
        } else {
          mat.transparent = true
        }

        const partMesh = new THREE.Mesh(geom, mat)

        const worldPos = new THREE.Vector3()
        const worldQuat = new THREE.Quaternion()
        const worldScale = new THREE.Vector3()
        child.matrixWorld.decompose(worldPos, worldQuat, worldScale)

        partMesh.position.copy(worldPos)
        partMesh.quaternion.copy(worldQuat)
        partMesh.scale.copy(worldScale)
        partMesh.castShadow = true
        partMesh.receiveShadow = true

        this.scene.add(partMesh)

        const relativePos = worldPos.clone().sub(mob.mesh.position)
        relativePos.y = Math.max(0, relativePos.y)

        const baseVel = mob.velocity.clone()
        const burstVel = new THREE.Vector3(
          (Math.random() - 0.5) * 3 + relativePos.x * 2.5,
          Math.random() * 3 + 2.0 + relativePos.y * 2.0,
          (Math.random() - 0.5) * 3 + relativePos.z * 2.5
        )
        const velocity = baseVel.add(burstVel)

        const angularVelocity = new THREE.Vector3(
          (Math.random() - 0.5) * 12,
          (Math.random() - 0.5) * 12,
          (Math.random() - 0.5) * 12
        )

        this.ragdolls.push({
          mesh: partMesh,
          velocity,
          angularVelocity,
          life: 1800,
          maxLife: 1800,
          resting: false
        })
      }
    })

    mob.mesh.visible = false
  }

  setDropManager(dropManager: BlockDropManager) {
    this.dropManager = dropManager
  }

  setCreeperExplosionHandler(handler: (position: THREE.Vector3) => void) {
    this.creeperExplosionHandler = handler
  }

  spawnCow(position: THREE.Vector3) {
    const cow = new Cow(this.scene, this.terrain, position, strawberryCowTex)
    return this.registerMob(cow)
  }

  spawnFineDog(position: THREE.Vector3) {
    const dog = new FineDog(this.scene, this.terrain, position)
    return this.registerMob(dog)
  }

  spawnPig(position: THREE.Vector3) {
    const pig = new Pig(this.scene, this.terrain, position, pigTex)
    return this.registerMob(pig)
  }

  spawnSheep(position: THREE.Vector3) {
    const sheep = new Sheep(this.scene, this.terrain, position, sheepTex, sheepFurTex)
    return this.registerMob(sheep)
  }

  spawnChicken(position: THREE.Vector3) {
    const chicken = new Chicken(this.scene, this.terrain, position, chickenTex)
    return this.registerMob(chicken)
  }

  spawnRabbit(position: THREE.Vector3) {
    const rabbit = new Rabbit(this.scene, this.terrain, position)
    return this.registerMob(rabbit)
  }

  spawnSproutMole(position: THREE.Vector3) {
    const mole = new SproutMole(this.scene, this.terrain, position)
    return this.registerMob(mole)
  }

  spawnHostileSproutMole(position: THREE.Vector3) {
    const mole = new HostileSproutMole(this.scene, this.terrain, position)
    return this.registerMob(mole)
  }

  spawnSomething(position: THREE.Vector3) {
    const entity = new Something(this.scene, this.terrain, position)
    return this.registerMob(entity)
  }

  spawnDefaultAnimals() {
    this.spawnCow(new THREE.Vector3(5, 40, 5))
    this.spawnCow(new THREE.Vector3(0, 40, 0))
    this.spawnCow(new THREE.Vector3(-5, 40, -5))
    this.spawnFineDog(new THREE.Vector3(3, 40, -4))
    this.spawnPig(new THREE.Vector3(8, 40, -2))
    this.spawnPig(new THREE.Vector3(-8, 40, 4))
    this.spawnSheep(new THREE.Vector3(6, 40, -8))
    this.spawnSheep(new THREE.Vector3(-3, 40, 7))
    this.spawnChicken(new THREE.Vector3(1, 40, 8))
    this.spawnChicken(new THREE.Vector3(-7, 40, -2))
    this.spawnChicken(new THREE.Vector3(9, 40, 5))
    this.spawnRabbit(new THREE.Vector3(-10, 40, -7))
    this.spawnRabbit(new THREE.Vector3(11, 40, -6))
  }

  resetToDefaultAnimals() {
    this.clearMobs()
    this.spawnDefaultAnimals()
  }

  clearMobs() {
    for (const mob of this.mobs) {
      this.scene.remove(mob.mesh)
    }
    this.mobs = []

    for (const part of this.ragdolls) {
      this.scene.remove(part.mesh)
      part.mesh.geometry.dispose()
      if (Array.isArray(part.mesh.material)) {
        part.mesh.material.forEach(m => m.dispose())
      } else {
        part.mesh.material.dispose()
      }
    }
    this.ragdolls = []

    this.hostileSpawnTimer = 5
    this.projectiles.clear()
  }

  updateRagdolls(delta: number) {
    const gravity = -18
    const bounceDecay = 0.5
    const friction = 0.8

    for (let i = this.ragdolls.length - 1; i >= 0; i--) {
      const part = this.ragdolls[i]
      part.life -= delta

      if (part.life <= 0) {
        this.scene.remove(part.mesh)
        part.mesh.geometry.dispose()
        if (Array.isArray(part.mesh.material)) {
          part.mesh.material.forEach(m => m.dispose())
        } else {
          part.mesh.material.dispose()
        }
        this.ragdolls.splice(i, 1)
        continue
      }

      if (part.resting) {
        if (part.life < 10) {
          const fadeRatio = part.life / 10
          const scaleScale = Math.min(1.0, fadeRatio * 1.5)
          part.mesh.scale.set(scaleScale, scaleScale, scaleScale)

          if (Array.isArray(part.mesh.material)) {
            part.mesh.material.forEach(m => {
              m.opacity = fadeRatio
            })
          } else {
            part.mesh.material.opacity = fadeRatio
          }
        }
        continue
      }

      part.velocity.y += gravity * delta
      part.mesh.position.addScaledVector(part.velocity, delta)

      part.mesh.rotateX(part.angularVelocity.x * delta)
      part.mesh.rotateY(part.angularVelocity.y * delta)
      part.mesh.rotateZ(part.angularVelocity.z * delta)

      const x = part.mesh.position.x
      const z = part.mesh.position.z
      const terrainY = this.terrain.getElevation(x, z)

      if (part.mesh.position.y < terrainY + 0.1) {
        part.mesh.position.y = terrainY + 0.1

        if (part.velocity.y < -0.5) {
          part.velocity.y = -part.velocity.y * bounceDecay
          part.velocity.x *= friction
          part.velocity.z *= friction
          part.angularVelocity.multiplyScalar(friction)
        } else {
          part.velocity.y = 0
          part.velocity.x *= 0.7
          part.velocity.z *= 0.7
          part.angularVelocity.multiplyScalar(0.7)

          if (part.velocity.lengthSq() < 0.01 && part.angularVelocity.lengthSq() < 0.01) {
            part.resting = true
            part.velocity.set(0, 0, 0)
            part.angularVelocity.set(0, 0, 0)
          }
        }
      }

      if (part.life < 10) {
        const fadeRatio = part.life / 10
        const scaleScale = Math.min(1.0, fadeRatio * 1.5)
        part.mesh.scale.set(scaleScale, scaleScale, scaleScale)

        if (Array.isArray(part.mesh.material)) {
          part.mesh.material.forEach(m => {
            m.opacity = fadeRatio
          })
        } else {
          part.mesh.material.opacity = fadeRatio
        }
      } else {
        part.mesh.scale.set(1.0, 1.0, 1.0)
        if (Array.isArray(part.mesh.material)) {
          part.mesh.material.forEach(m => {
            m.opacity = 1.0
          })
        } else {
          part.mesh.material.opacity = 1.0
        }
      }
    }
  }

  getAnimalType(mob: Mob): AnimalMobType | null {
    if (mob instanceof Cow) return 'cow'
    if (mob instanceof FineDog) return 'fineDog'
    if (mob instanceof Pig) return 'pig'
    if (mob instanceof Sheep) return 'sheep'
    if (mob instanceof Chicken) return 'chicken'
    if (mob instanceof Rabbit) return 'rabbit'
    if (mob instanceof SproutMole) return 'sproutMole'
    return null
  }

  spawnAnimal(type: AnimalMobType, position: THREE.Vector3) {
    switch (type) {
      case 'cow':
        return this.spawnCow(position)
      case 'fineDog':
        return this.spawnFineDog(position)
      case 'pig':
        return this.spawnPig(position)
      case 'sheep':
        return this.spawnSheep(position)
      case 'chicken':
        return this.spawnChicken(position)
      case 'rabbit':
        return this.spawnRabbit(position)
      case 'sproutMole':
        return this.spawnSproutMole(position)
      default:
        return null
    }
  }

  getSavedPosition(value: SavedVector | undefined) {
    const x = value?.x
    const y = value?.y
    const z = value?.z

    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof z !== 'number' ||
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(z)
    ) {
      return null
    }

    return new THREE.Vector3(x, y, z)
  }

  clampHealth(value: unknown, maxHealth: number) {
    const health = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(health)) return maxHealth
    return Math.max(0, Math.min(maxHealth, health))
  }

  serializeAnimalsList(mobsList: Mob[]) {
    const animals: SavedAnimal[] = []

    for (const mob of mobsList) {
      if (!mob.isAlive || mob.isHostile || mob.isDying) continue

      const type = this.getAnimalType(mob)
      if (!type) continue

      animals.push({
        type,
        position: {
          x: mob.mesh.position.x,
          y: mob.mesh.position.y,
          z: mob.mesh.position.z
        },
        health: mob.health,
        following: mob.isFollowing(),
        boarded: mob.isBoarded,
        arkProgress: {
          boarded: mob.isBoarded
        }
      })
    }

    return JSON.stringify({ version: 1, animals })
  }

  serializeAnimals() {
    return this.serializeAnimalsList(this.mobs)
  }

  deserializeAnimalsToList(value: string | null, followTarget?: THREE.Object3D): Mob[] {
    if (!value) return []

    try {
      const save = JSON.parse(value) as SavedAnimalList | SavedAnimal[]
      const animals = Array.isArray(save) ? save : save.animals
      if (!Array.isArray(animals)) return []

      const result: Mob[] = []
      for (const saved of animals) {
        if (!saved.type) continue

        const position = this.getSavedPosition(saved.position)
        if (!position) continue

        const mob = this.spawnAnimal(saved.type, position)
        if (!mob) continue

        mob.health = this.clampHealth(saved.health, mob.maxHealth)
        if (mob.health <= 0) {
          mob.despawn()
          continue
        }

        if (saved.following && followTarget) {
          mob.setFollowTarget(followTarget)
        }

        mob.isBoarded = Boolean(saved.boarded ?? saved.arkProgress?.boarded)

        const idx = this.mobs.indexOf(mob)
        if (idx !== -1) {
          this.mobs.splice(idx, 1)
        }
        result.push(mob)
      }

      return result.filter(mob => mob.isAlive)
    } catch {
      return []
    }
  }

  deserializeAnimals(value: string | null, followTarget?: THREE.Object3D) {
    if (!value) return false

    try {
      const save = JSON.parse(value) as SavedAnimalList | SavedAnimal[]
      const animals = Array.isArray(save) ? save : save.animals
      if (!Array.isArray(animals)) return false

      this.clearMobs()

      for (const saved of animals) {
        if (!saved.type) continue

        const position = this.getSavedPosition(saved.position)
        if (!position) continue

        const mob = this.spawnAnimal(saved.type, position)
        if (!mob) continue

        mob.health = this.clampHealth(saved.health, mob.maxHealth)
        if (mob.health <= 0) {
          mob.despawn()
          continue
        }

        if (saved.following && followTarget) {
          mob.setFollowTarget(followTarget)
        }

        mob.isBoarded = Boolean(saved.boarded ?? saved.arkProgress?.boarded)
      }

      this.mobs = this.mobs.filter(mob => mob.isAlive)
      return true
    } catch {
      return false
    }
  }

  spawnZombie(position: THREE.Vector3) {
    const zombie = new Zombie(this.scene, this.terrain, position)
    return this.registerMob(zombie)
  }

  spawnCreeper(position: THREE.Vector3) {
    const creeper = new Creeper(this.scene, this.terrain, position)
    creeper.onExplode = this.creeperExplosionHandler
    return this.registerMob(creeper)
  }

  spawnSpider(position: THREE.Vector3) {
    const spider = new Spider(this.scene, this.terrain, position)
    return this.registerMob(spider)
  }

  spawnSkeletonGunner(position: THREE.Vector3) {
    const skeleton = new SkeletonGunner(this.scene, this.terrain, position)
    skeleton.projectiles = this.projectiles
    return this.registerMob(skeleton)
  }

  getMobFromObject(object: THREE.Object3D) {
    return this.mobs.find(mob => {
      let found = false
      mob.mesh.traverse(child => {
        if (child === object) found = true
      })
      return found
    }) ?? null
  }

  update(
    delta: number,
    target?: THREE.Object3D,
    onHostileAttack?: (amount: number) => void,
    isNight = false,
    getHeldItem?: () => InventoryItemType | null
  ) {
    this.updateNightSpawns(delta, target, onHostileAttack, isNight)
    if (target) {
      this.updatePassiveSpawns(delta, target.position)
    }
    this.projectiles.update(delta, target, onHostileAttack)
    this.updateRagdolls(delta)

    const held = getHeldItem ? getHeldItem() : null
    const holdingFood = held !== null && isFoodItem(held)

    let anyDied = false
    for (const mob of this.mobs) {
      mob.lookTarget = target ?? null

      if (mob instanceof HostileMob) {
        mob.setCombatTarget(target ?? null, onHostileAttack ?? null)
        mob.environmentIsNight = isNight
        // vanilla: only some undead burn at dawn
        if (!isNight && !mob.isDying && mob.burnsInSunlight) {
          mob.takeDamage(1)
        }
      } else {
        // passives notice food in your hand
        mob.temptTarget =
          holdingFood &&
          target &&
          mob.mesh.position.distanceTo(target.position) < 9
            ? target
            : null
      }

      mob.update(delta)
      if ((mob.isDying || !mob.isAlive) && !mob.dropsSpawned) {
        this.spawnMobDrops(mob)
        mob.dropsSpawned = true
      }
      if (!mob.isAlive) anyDied = true
    }
    
    // Clean up despawned mobs (in place, only when one actually died)
    if (anyDied) {
      for (let i = this.mobs.length - 1; i >= 0; i--) {
        if (!this.mobs[i].isAlive) this.mobs.splice(i, 1)
      }
    }
  }

  updatePassiveSpawns(delta: number, playerPosition: THREE.Vector3) {
    if (this.terrain.dimension === 'nether') return
    let passiveCount = 0
    for (const mob of this.mobs) {
      if (!mob.isHostile && mob.isAlive && !mob.isDying) passiveCount++
    }
    if (passiveCount >= this.maxPassives) return

    this.passiveSpawnTimer -= delta
    if (this.passiveSpawnTimer > 0) return

    this.spawnPassiveNear(playerPosition)
    this.passiveSpawnTimer =
      this.passiveSpawnInterval + Math.random() * this.passiveSpawnInterval
  }

  spawnPassiveNear(playerPosition: THREE.Vector3) {
    for (let attempt = 0; attempt < 10; attempt++) {
      const angle = Math.random() * Math.PI * 2
      const radius = 20 + Math.random() * 16
      const x = playerPosition.x + Math.cos(angle) * radius
      const z = playerPosition.z + Math.sin(angle) * radius
      const terrainY = this.terrain.getElevation(x, z)
      const groundY = terrainY + 0.5
      const waterSurface = this.terrain.getWaterSurfaceYAt(x, z)

      if (waterSurface !== null && groundY < waterSurface + 0.2) continue

      const position = new THREE.Vector3(x, groundY, z)
      const biome = this.terrain.noise.getBiomeAt(x, z, terrainY)
      if (biome === BiomeType.omori) {
        return this.spawnSproutMole(position)
      }

      const roll = Math.random()
      if (roll < 0.25) return this.spawnCow(position)
      if (roll < 0.45) return this.spawnPig(position)
      if (roll < 0.65) return this.spawnSheep(position)
      if (roll < 0.85) return this.spawnChicken(position)
      if (roll < 0.95) return this.spawnRabbit(position)
      return this.spawnFineDog(position)
    }
    return null
  }

  updateNightSpawns(
    delta: number,
    target: THREE.Object3D | undefined,
    onHostileAttack: ((amount: number) => void) | undefined,
    isNight: boolean
  ) {
    const shouldSpawn = isNight || this.terrain.dimension === 'nether'
    if (!target || !onHostileAttack || !shouldSpawn) {
      this.hostileSpawnTimer = Math.min(this.hostileSpawnTimer, 2)
      return
    }

    let hostileCount = 0
    for (const mob of this.mobs) {
      if (mob.isHostile && mob.isAlive && !mob.isDying) hostileCount++
    }
    if (hostileCount >= this.maxHostiles) return

    this.hostileSpawnTimer -= delta
    if (this.hostileSpawnTimer > 0) return

    this.spawnHostileNear(target.position)
    this.hostileSpawnTimer =
      this.hostileSpawnInterval + Math.random() * this.hostileSpawnInterval
  }

  spawnHostileNear(playerPosition: THREE.Vector3) {
    for (let attempt = 0; attempt < 10; attempt++) {
      const angle = Math.random() * Math.PI * 2
      const radius = 20 + Math.random() * 16
      const x = playerPosition.x + Math.cos(angle) * radius
      const z = playerPosition.z + Math.sin(angle) * radius

      let groundY = -1
      if (this.terrain.dimension === 'nether') {
        for (let y = 14; y < 55; y++) {
          const block = this.terrain.getCustomBlock(Math.round(x), y, Math.round(z))
          const blockAbove = this.terrain.getCustomBlock(Math.round(x), y + 1, Math.round(z))
          const blockTwoAbove = this.terrain.getCustomBlock(Math.round(x), y + 2, Math.round(z))

          const isSolid = block && block.placed && block.type !== BlockType.lava
          const isAirAbove = (!blockAbove || !blockAbove.placed) && (!blockTwoAbove || !blockTwoAbove.placed)

          if (isSolid && isAirAbove) {
            groundY = y + 0.5
            break
          }
        }
      } else {
        const terrainY = this.terrain.getElevation(x, z)
        groundY = terrainY + 0.5
        const waterSurface = this.terrain.getWaterSurfaceYAt(x, z)
        if (waterSurface !== null && groundY < waterSurface + 0.2) continue
      }

      if (groundY === -1) continue

      const position = new THREE.Vector3(x, groundY, z)

      if (this.terrain.dimension === 'nether') {
        const roll = Math.random()
        if (roll < 0.32) return this.spawnZombie(position)
        if (roll < 0.56) return this.spawnCreeper(position)
        if (roll < 0.78) return this.spawnSpider(position)
        return this.spawnSkeletonGunner(position)
      }

      const terrainY = this.terrain.getElevation(x, z)
      const biome = this.terrain.noise.getBiomeAt(x, z, terrainY)
      if (biome === BiomeType.omori) {
        return Math.random() < 0.6
          ? this.spawnHostileSproutMole(position)
          : this.spawnSomething(position)
      }

      const roll = Math.random()
      if (roll < 0.32) return this.spawnZombie(position)
      if (roll < 0.56) return this.spawnCreeper(position)
      if (roll < 0.78) return this.spawnSpider(position)
      return this.spawnSkeletonGunner(position)
    }

    const fallback = playerPosition.clone()
    fallback.x += 18
    fallback.z += 8
    fallback.y = this.terrain.getElevation(fallback.x, fallback.z) + 0.5
    return this.spawnCreeper(fallback)
  }

  spawnMobDrops(mob: Mob) {
    if (!this.dropManager) return

    for (const drop of mob.getDrops()) {
      const position = mob.mesh.position.clone()
      position.x += (Math.random() - 0.5) * 0.8
      position.z += (Math.random() - 0.5) * 0.8
      position.y += 0.4
      this.dropManager.spawn(position, drop)
    }
  }
}
